import { Autocomplete, Box, TextField } from "@mui/material";
import { strongAutocompletePaperProps } from "components/common/dropdownPaper";
import {
  type TimezoneOption,
  useGetTimezonesQuery,
} from "features/integrationsBeta/platformEnums";

interface Props {
  value: string;
  onChange: (tz: string) => void;
  disabled?: boolean;
  label?: string;
  size?: "small" | "medium";
  sx?: object;
}

/**
 * Searchable IANA timezone picker (Autocomplete over the backend /timezones/
 * list, so it matches server-side validation). Shows the current DST
 * abbreviation next to each zone.
 */
export default function TimezoneSelect({
  value,
  onChange,
  disabled,
  label = "Timezone",
  size = "small",
  sx,
}: Props) {
  const { data: zones = [] } = useGetTimezonesQuery();
  const selected = zones.find((z) => z.value === value) ?? null;

  return (
    <Autocomplete<TimezoneOption>
      size={size}
      disabled={disabled}
      componentsProps={strongAutocompletePaperProps}
      options={zones}
      value={selected}
      onChange={(_, v) => {
        if (v) onChange(v.value);
      }}
      getOptionLabel={(o) => o.value}
      isOptionEqualToValue={(o, v) => o.value === v.value}
      renderOption={(props, o) => (
        <Box component="li" {...props} key={o.value}>
          {o.value}
          {o.abbreviation ? (
            <Box
              component="span"
              sx={{ ml: 1, color: "text.secondary", fontSize: "0.8em" }}
            >
              {o.abbreviation}
            </Box>
          ) : null}
        </Box>
      )}
      sx={{ minWidth: 280, ...sx }}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}
